// src/pages/b2b/sections/ProductDetailB2BVariants.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";

// ✅ use ProductCard
import ProductCard from "../../../../GlobalFunction/ProductCard";

function publicUrl(path) {
  return "/" + String(path || "").replace(/^\/+/, "");
}

function asBoolFavourite(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (v === 1 || v === "1") return true;
  if (v === 0 || v === "0") return false;
  return false;
}

export default function ProductDetailB2BVariants({
  variants = [],
  value,
  onChange,
  selectedA,
  selectedB,
  selectedC,
  onClearA,
  onClearB,
  onClearC,
  lang,

  // ✅ allow favourite actions in variant list
  userID = "",
  onVariantFavouriteChange, // (nextBool, productID) => void (optional)
}) {
  const placeholder = publicUrl("img/empty.webp");
  // desktop baseline
  const BASE_CARD_W_DESKTOP = 180;
  const IMG_H_DESKTOP = 120;
  const GAP_DESKTOP = 16;

  // mobile tuning
  const BASE_CARD_W_MOBILE = 118;
  const IMG_H_MOBILE = 84;
  const GAP_MOBILE = 10;

  const scrollerRef = useRef(null);
  const containerRef = useRef(null);

  // map of variant id -> card element
  const cardRefs = useRef(new Map());
  const setCardRef = (id) => (el) => {
    const map = cardRefs.current;
    if (!id) return;
    if (el) map.set(id, el);
    else map.delete(id);
  };

  const [cardWidth, setCardWidth] = useState(BASE_CARD_W_DESKTOP);
  const [imgH, setImgH] = useState(IMG_H_DESKTOP);
  const [gap, setGap] = useState(GAP_DESKTOP);
  const [isScrollable, setIsScrollable] = useState(false);

  // ✅ Local favourite overrides: { [productID]: boolean }
  const [favOverride, setFavOverride] = useState({});

  // When variants arrive/update: seed override for any ids we haven't seen yet
  useEffect(() => {
    const nextSeed = {};
    for (const v of Array.isArray(variants) ? variants : []) {
      const id = String(v?.id || v?.productID || "").trim();
      if (!id) continue;

      // only seed if not already overridden
      if (favOverride[id] == null) {
        nextSeed[id] = asBoolFavourite(v?.favourite);
      }
    }

    if (Object.keys(nextSeed).length) {
      setFavOverride((prev) => ({ ...nextSeed, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  // --------- mouse drag-to-scroll ----------
  const DRAG_THRESHOLD = 6; // px

  const dragRef = useRef({
    active: false,
    pointerId: null,
    captured: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const checkScrollable = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setIsScrollable(el.scrollWidth > el.clientWidth + 2);
  };

  // computed "mobile" based on container width
  const isMobileRef = useRef(false);

  useEffect(() => {
    const wrap = containerRef.current;
    if (!wrap) return;

    const updateLayout = () => {
      const innerW = wrap.clientWidth;
      const isMobile = innerW < 520;
      isMobileRef.current = isMobile;

      const baseW = isMobile ? BASE_CARD_W_MOBILE : BASE_CARD_W_DESKTOP;
      const g = isMobile ? GAP_MOBILE : GAP_DESKTOP;
      const h = isMobile ? IMG_H_MOBILE : IMG_H_DESKTOP;

      const minW = isMobile ? 96 : 140;
      const computedW = Math.max(minW, Math.min(baseW, baseW));

      setGap(g);
      setImgH(h);
      setCardWidth(computedW);

      requestAnimationFrame(checkScrollable);
    };

    updateLayout();
    const ro = new ResizeObserver(updateLayout);
    ro.observe(wrap);
    window.addEventListener("resize", updateLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateLayout);
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(checkScrollable);
  }, [variants.length]);

  const step = useMemo(() => cardWidth + gap, [cardWidth, gap]);

  const scrollByOne = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // pointer drag
  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;

    const el = scrollerRef.current;
    if (!el) return;

    dragRef.current.active = true;
    dragRef.current.pointerId = e.pointerId;
    dragRef.current.captured = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startScrollLeft = el.scrollLeft;
    dragRef.current.moved = false;
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const el = scrollerRef.current;
    if (!el) return;

    const dx = e.clientX - dragRef.current.startX;

    if (!dragRef.current.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;

      dragRef.current.moved = true;

      if (!dragRef.current.captured) {
        try {
          e.currentTarget.setPointerCapture(dragRef.current.pointerId);
          dragRef.current.captured = true;
        } catch {}
      }
    }

    el.scrollLeft = dragRef.current.startScrollLeft - dx;

    try {
      e.preventDefault();
    } catch {}
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.active) return;

    dragRef.current.active = false;

    if (dragRef.current.captured) {
      try {
        e.currentTarget.releasePointerCapture(dragRef.current.pointerId);
      } catch {}
    }

    dragRef.current.pointerId = null;
    dragRef.current.captured = false;

    requestAnimationFrame(checkScrollable);
  };

  // prevent click selection after a drag
  const safeClick = (fn) => {
    if (dragRef.current.moved) return;
    fn?.();
  };

  // center when few
  const shouldCenter = variants.length <= 3;

  const title = {
    ZH: "產品系列:",
    EN: "Variants:",
    JP: "製品シリーズ:",
  };

  // auto-scroll selected into view
  const scrollSelectedIntoView = (behavior = "auto") => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    if (!value) return;

    if (shouldCenter) return;
    if (scroller.scrollWidth <= scroller.clientWidth + 2) return;

    const cardEl = cardRefs.current.get(value);
    if (!cardEl) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();

    const currentScrollLeft = scroller.scrollLeft;

    const cardLeftInContent =
      currentScrollLeft + (cardRect.left - scrollerRect.left);
    const cardCenterInContent = cardLeftInContent + cardRect.width / 2;

    const isMobile = !!isMobileRef.current;

    const LEFT_PAD = 8;
    const targetScrollLeft = isMobile
      ? cardCenterInContent - scroller.clientWidth / 2
      : cardLeftInContent - LEFT_PAD;

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    const clamped = Math.max(0, Math.min(maxScrollLeft, targetScrollLeft));

    scroller.scrollTo({ left: clamped, behavior });
  };

  useEffect(() => {
    if (!variants?.length) return;

    requestAnimationFrame(() => {
      scrollSelectedIntoView("auto");
      requestAnimationFrame(() => scrollSelectedIntoView("auto"));
      setTimeout(() => scrollSelectedIntoView("auto"), 120);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, variants.length, cardWidth, gap]);

  // ✅ handle favourite changes coming from ProductCard
  const handleFavChange = (nextBool, productID) => {
    const pid = String(productID || "").trim();
    if (!pid) return;

    // keep local UI correct immediately
    setFavOverride((prev) => ({ ...prev, [pid]: !!nextBool }));

    // bubble up for parent to sync detail page state if needed
    onVariantFavouriteChange?.(!!nextBool, pid);
  };

  return (
    <Box sx={{ maxWidth: "100%" }}>
      {/* Header + active-filter chips */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontFamily: "LINE Sans TW" }}
        >
          {title[lang]}
        </Typography>

        {selectedA && (
          <Chip
            label={selectedA}
            size="small"
            variant="outlined"
            onDelete={onClearA}
            sx={{
              borderRadius: 999,
              height: 30,
              fontSize: 13,
              fontWeight: 600,
              px: 1.5,
            }}
          />
        )}

        {selectedB && (
          <Chip
            label={selectedB}
            size="small"
            variant="outlined"
            onDelete={onClearB}
            sx={{
              borderRadius: 999,
              height: 30,
              fontSize: 13,
              fontWeight: 600,
              px: 1.5,
            }}
          />
        )}

        {selectedC && (
          <Chip
            label={selectedC}
            size="small"
            variant="outlined"
            onDelete={onClearC}
            sx={{
              borderRadius: 999,
              height: 30,
              fontSize: 13,
              fontWeight: 600,
              px: 1.5,
            }}
          />
        )}
      </Box>

      {/* Slider wrapper */}
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          py: 1.5,
        }}
      >
        {/* Scroll area */}
        <Box
          ref={scrollerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },

            touchAction: "pan-x",
            userSelect: "none",
            cursor: isScrollable ? "grab" : "default",
            "&:active": { cursor: isScrollable ? "grabbing" : "default" },

            px: { xs: 4.5, md: 5.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: `${gap}px`,
              alignItems: "stretch",
              justifyContent: shouldCenter ? "center" : "flex-start",
              minWidth: "100%",
              py: 0.5,
            }}
          >
            {variants.map((v) => {
              const selected = v.id === value;
              const imgSrc = v.image || placeholder;

              const pid = String(v?.id || v?.productID || "").trim();

              // ✅ resolved favourite value:
              // 1) local override (latest user action)
              // 2) backend-provided v.favourite (if detail API supports it)
              const resolvedFav =
                pid && favOverride[pid] != null
                  ? favOverride[pid]
                  : asBoolFavourite(v?.favourite);

              const productForCard = {
                ...v,
                productID: v.id, // important for favourite key
                name: v.name,
                image: imgSrc,
                favourite: resolvedFav ? 1 : 0,
              };

              return (
                <Box
                  key={v.id}
                  ref={setCardRef(v.id)}
                  sx={{
                    flex: "0 0 auto",
                    width: cardWidth,
                    borderRadius: 2,
                    outline: selected ? "2px solid #1976d2" : "1px solid",
                    outlineColor: selected ? "#1976d2" : "divider",
                  }}
                >
                  <ProductCard
                    product={productForCard}
                    to="" // ✅ no navigation in scroller
                    view="grid"
                    compact
                    titleLines={2}
                    imageHeight={imgH}
                    disableHoverSwap
                    userID={userID}
                    showFavourite={true}
                    onClickCard={() => safeClick(() => onChange?.(v.id))}
                    onFavouriteChange={handleFavChange}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Arrow buttons */}
        {variants.length > 0 && (
          <>
            <IconButton
              onClick={() => scrollByOne(-1)}
              sx={{
                position: "absolute",
                left: 4,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "background.paper",
                boxShadow: 1,
                width: { xs: 30, sm: 34, md: 38 },
                height: { xs: 30, sm: 34, md: 38 },
                zIndex: 2,
              }}
            >
              <ChevronLeftRounded />
            </IconButton>

            <IconButton
              onClick={() => scrollByOne(1)}
              sx={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "background.paper",
                boxShadow: 1,
                width: { xs: 30, sm: 34, md: 38 },
                height: { xs: 30, sm: 34, md: 38 },
                zIndex: 2,
              }}
            >
              <ChevronRightRounded />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
}
